import type {Field008Code, Field008Definitions, Field008Label, Fond} from 'typings/portaro.be.types';
import type {Component} from 'typings/portaro.fe.types';
import type {
    ModalWindowActions,
    ModalWindowBindings,
    ModalWindowResolveValues
} from '../../../commons/conversation/conversation';
import {getCodesByPosition, getLabelsAndCodesByDocumentTypeCode} from '../utils/utils';
import type {ILogService, IOnDestroy, IOnInit, IScope} from 'angular';
import {createPayloadActionResponse} from '../../../commons/conversation/modal-utils';
import type {Field8EditorService} from './field8-editor.service';
import type {Field8Model} from '../utils/types';
import type {Field8EditorServiceFactory} from './field8-editor-service.factory';
import {
    IncorrectSectionFormatError,
    MissingDocumentTypesError,
    UnknownSectionsCodesError
} from '../utils/field8-errors';
import type ModalConversationService from '../../../commons/conversation/modal-conversation.service';
import KpUniversalForm from '../../../commons/value-editors/kp-universal-form/KpUniversalForm.svelte';
import type {KpUniversalFormSettings} from '../../../commons/value-editors/kp-universal-form/types';
import type { ObjectValueEditorField } from 'src/commons/value-editors/meta-editors/object/types';
import type {FormController} from '../../../commons/value-editors/internal/forms/types';
import type {Subscription} from 'rxjs';

export interface Field8EditorModalModel {
    value: string;
    fond: Fond;
    definitions: Field008Definitions;
}

export class Field8EditorModalComponent implements Component<ModalWindowBindings<Field8EditorModalModel>> {
    public static componentName = 'field8EditorModal';

    public bindings = {
        resolve: '<'
    } as const;
    public controller = Field8EditorModalComponentController;
    public templateUrl = require('./field8-editor-modal.component.tpl.ftl');
}

class Field8EditorModalComponentController implements ModalWindowBindings<Field8EditorModalModel>, IOnInit, IOnDestroy {
    public resolve: ModalWindowResolveValues<Field8EditorModalModel>;

    public kpUniversalFormComponent = KpUniversalForm;

    public modalWindowActions: ModalWindowActions;
    public model: Field8EditorModalModel;

    public formModel: Field8Model;
    public formSettings: KpUniversalFormSettings<Field8Model, ObjectValueEditorField<Field8Model>[]>; // form fields definition
    public isFormValid: boolean;

    private formController: FormController<Field8Model>;
    private subscription: Subscription;
    private editorService: Field8EditorService; // field 008 editor logic

    private definitions: Field008Definitions;
    private models: Map<string, Field8Model>; // MAP documentTypeCode -> formModel for documentTypeCode
    private error: Error | null = null;

    private lastDocumentTypeCode: string;
    private lastPublicationStatusCode: string;

    /*@ngInject*/
    constructor(private field8EditorServiceFactory: Field8EditorServiceFactory,
                private modalConversationService: ModalConversationService,
                private $log: ILogService,
                private $scope: IScope) {
    }

    public async $onInit() {
        this.modalWindowActions = this.resolve.modalWindowActions;
        this.model = this.resolve.model;
        this.definitions = this.model.definitions;

        this.editorService = this.field8EditorServiceFactory.createService(this.definitions, this.$log);

        const allowedDocumentTypes = this.definitions.documentTypes
            .filter((documentType) => this.model.fond.documentTypes.includes(documentType.code));

        if (allowedDocumentTypes.length === 0) {
            this.error = new Error('Žádné povolené typy dokumentu - změňte nastavení fondu');
            this.$log.error(this.error, 'Fond editovaného záznamu nemá nastavené žádné povolené typy dokumentu');
            return; // show error and stop form initialization
        }

        this.formSettings = this.editorService.initializeFormsFirstSegment(allowedDocumentTypes);
        try {
            this.models = this.editorService.parseCode(this.model.value); // parse code for every document type
        } catch (error: unknown) {
            await this.handleParsingErrors(error);
        }

        if (this.models.size === 0) { // empty or invalid field8 value (because no model was parsed)
            const defaultDocumentType = allowedDocumentTypes[0];
            this.formModel = this.editorService.createDefaultSharedValuesModel(defaultDocumentType);
            this.updateFormAndModel(this.formModel.documentType.code);
        } else { // editing existing field8 value
            const allowedModels = Array.from(this.models.values())
                .filter((model) => allowedDocumentTypes.includes(model.documentType)); // at least 1 document type is allowed => never empty
            this.formModel = allowedModels[0]; // get first available model
            this.updateForm(this.formModel.documentType.code);
        }

        this.lastDocumentTypeCode = this.formModel.documentType.code;
        this.lastPublicationStatusCode = this.formModel.publicationStatus.code;
    }

    public $onDestroy() {
        this.subscription?.unsubscribe();
    }

    public onChange() {
        if (this.formModel.documentType.code !== this.lastDocumentTypeCode) { // changed document type
            this.updateFormAndModel(this.formModel.documentType.code); // reset form based on new document type
            this.lastDocumentTypeCode = this.formModel.documentType.code; // save last document type
        }
        if (this.formModel.publicationStatus.code !== this.lastPublicationStatusCode) { // changed publication status
            this.editorService.setDefaultDatesForPublicationStatusToModel(this.formModel);
            this.formSettings = this.editorService.updateFormsDate2Label(this.formSettings, this.formModel.publicationStatus.code);
            this.lastPublicationStatusCode = this.formModel.publicationStatus.code; // save last publication status
        }
    }

    public getFormController(formController: FormController<Field8Model>) {
        this.formController = formController;
        this.subscription = this.formController.getFieldState$().subscribe((state) => {
            this.isFormValid = state.valid;
            this.$scope.$applyAsync();
        });
    }

    public submit() {
        if (!this.isFormValid || this.hasError) {
            return;
        }

        this.modalWindowActions.submitPromise(Promise.resolve(createPayloadActionResponse(this.editorService.serializeModel(this.formModel))));
    }

    public get hasError(): boolean {
        return !!this.error;
    }

    public getError(): Error {
        return this.error;
    }

    private updateForm(code: string) {
        const [labels, codes] = getLabelsAndCodesByDocumentTypeCode(this.definitions, code);
        const formSettings = this.editorService.initializeFormsSecondSegment(this.formSettings, labels, codes); // change form according to document type
        this.formSettings = this.editorService.updateFormsDate2Label(formSettings, this.formModel.publicationStatus.code); // change date2 label according to publication status
    }

    private updateFormAndModel(code: string) {
        const [labels, codes] = getLabelsAndCodesByDocumentTypeCode(this.definitions, code);
        this.formSettings = this.editorService.initializeFormsSecondSegment(this.formSettings, labels, codes); // change form according to document type
        this.updateModel(labels, codes);
    }

    private updateModel(labels: Field008Label[], codes: Field008Code[]) {
        if (this.models.has(this.formModel.documentType.code)) { // get existing model corresponding to document type
            const model = this.models.get(this.formModel.documentType.code);
            // copy data from first segment (shared values)
            model.documentType = this.formModel.documentType;
            model.cataloguingSource = this.formModel.cataloguingSource
            model.placeOfPublication = this.formModel.placeOfPublication
            model.language = this.formModel.language
            model.date2 = this.formModel.date2
            model.date1 = this.formModel.date1
            model.publicationStatus = this.formModel.publicationStatus
            model.recordModification = this.formModel.recordModification
            model.fileCreationDate = this.formModel.fileCreationDate
            this.formModel = model; // update form model
        } else { // create new model
            this.formModel = { // take shared values
                fileCreationDate: this.formModel.fileCreationDate,
                documentType: this.formModel.documentType,
                cataloguingSource: this.formModel.cataloguingSource,
                placeOfPublication: this.formModel.placeOfPublication,
                language: this.formModel.language,
                date2: this.formModel.date2,
                date1: this.formModel.date1,
                publicationStatus: this.formModel.publicationStatus,
                recordModification: this.formModel.recordModification
            };
            labels
                .filter((label) => label.isDefined) // some undefined positions have default values
                .forEach((label) => this.editorService.setPositionsDefaultValueToModel(this.formModel, label.order, getCodesByPosition(codes, label.order))); // set defaults in second segment
        }
        this.models.set(this.formModel.documentType.code, this.formModel); // save updated model
    }

    private async handleParsingErrors(error: unknown) {
        this.$log.error(error);
        if (error instanceof IncorrectSectionFormatError) {
            const errorMessage = `hodnota [${error.sectionValue}] sekce [${error.section}] má chybný formát`;
            await this.askUserToUseDefaults(errorMessage);
        } else if (error instanceof UnknownSectionsCodesError) {
            const errorMessage = `pro kódy ze sekcí [${error.sectionsWithUnknownCodes.join(', ')}] nebyla nalezena definovaná hodnota`;
            await this.askUserToUseDefaults(errorMessage);
        } else if (error instanceof MissingDocumentTypesError) {
            const errorMessage = 'chybí definice kódů pro typy dokumentu';
            await this.askUserToUseDefaults(errorMessage);
        } else {
            throw new Error('Unknown field8 parsing error');
        }
    }

    private async askUserToUseDefaults(errorMessage: string) {
        await this.modalConversationService.confirmationDialog({text: `Pole 008 má chybnou strukturu (${errorMessage}). Chcete nastavit výchozí hodnoty?`})
        this.models = new Map();
    }
}